// vm-verb-table.ts — WHICH VM VERB OWNS A RELEASE IN THIS CELL, and what the
// framework pin MEANS for the boot that verb will run.
//
// #1272 (and the other half of #790). fork-resume-gate.ts answers the
// LIFECYCLE question — which activation lane owns a status. This module
// answers the MACHINE question next to it: a release in this status, with a
// VM in this state, is given a VM back by WHICH verb?
//
// THE MEASURED GAP. On 2026-09-09 REL-096 `daag-full-run` reached
// `status='active'` with `vm_state='gone'` (#1185: a stale SSM framework-pin
// override booted the wrong bundle, the boot census failed, the console
// reaped the instance, vm-reconcile marked the row gone) and EVERY verb
// refused, each one correctly:
//
//   POST /vm/relaunch  409 vm-gone   — "nothing to snapshot or terminate;
//                                      the next RM turn re-provisions"
//   POST /vm/resume    no-op         — `!release.vm_stopped_at` -> "not
//                                      stopped — nothing to do"
//   POST /start        409           — `status !== 'ideating'`
//   POST /retry-boot   409 twice     — ideating-only AND fork-only
//   ensureReleaseVm('rm-turn')       — needed an RM turn, and the release had
//                                      0 sessions BECAUSE the VM was gone (that
//                                      cause is deleted with the RM, P16)
//
// relaunchVm's own design comment records #790 discovering this cell, on this
// release, listing the same dead routes — and then closing it only for a LIVE
// instance, pushing the gone case to "Start / the next RM turn". Start is
// ideating-only and an RM turn needs a session. So `active` + no instance was
// left unowned, and the defect LOCKS THE DOOR BEHIND ITSELF: it bricks the
// boot, the reap moves the row to gone, the operator's own recovery verb now
// refuses, and the remedy that refusal names cannot fire.
//
// WHY A NEW VERB AND NOT A WIDENED GATE — from the state machine, not from
// convenience. The three existing VM verbs are all transitions ON AN EXISTING
// MACHINE: relaunch REPLACES one (snapshot -> terminate -> clear -> provision),
// resume STARTS a stopped one, stop STOPS a running one. Creating a machine
// where there is none was never a verb here because it was always a SIDE
// EFFECT of a status transition (create's eager ensure, start, the first RM
// turn). Once an Active release can lose its machine without a status change,
// "create where there is none" is a standalone transition and needs its own
// verb. Widening `relaunch` would make its one safety invariant conditional
// ("forensic snapshot FIRST, always" — with nothing to snapshot the verb's
// name and its contract both stop being true); widening `retryBoot` would
// falsify its own stated model ("an Active release has its VM") and drag a
// parked-fork retry ladder and a no-flip contract into a cell that needs
// neither.
//
// PURE BY CONSTRUCTION — facts in, verb out. No DB handle, no clock, no I/O,
// so the whole matrix is driven offline by vm-provision.test.mjs and the
// console below it holds exactly one copy of each sentence.

// WHAT "forensic snapshot FIRST" IS A PROPERTY OF (#3509, orchestrator
// ruling 2026-09-21 — not an owner ruling).
//
// The vm/relaunch cell below has said "forensic snapshot FIRST" since #790,
// and that sentence READ AS A CONSOLE-WIDE GUARANTEE it never was. A reader
// of this table, or of the audit trail, concluded the console never destroys
// a release VM's disk without a forensic snapshot. The console holds four
// terminate paths — this verb's, in releases.ts, and the reaper's three —
// and they neither all snapshotted nor all said so.
//
// The ruling refused both answers #3509 offered ("console-wide invariant" and
// "per-verb property") because the axis is wrong. What died in the near miss
// that sized this was the release's WORK — a database — on a box whose
// usefulness had ended while the work's had not. That is not VM-local state,
// and it is not a property of a verb. THE SUBJECT DECIDES:
//
//   RELEASE VM  -> snapshot FIRST, or audit explicitly that it did not, and
//                  why. Owed by EVERY path, whatever verb reached it.
//   REPLAY VM   -> disposable observation env by doctrine; no snapshot owed,
//                  and the audit says so rather than omitting the field.
//   RM SANDBOX  -> ephemeral by construction, out of scope, declared in
//                  rm-substrate.ts's own text.
//
// The invariant itself lives in ONE place — `forensicDisposition` in
// reaper.ts — and this cell's wording now points at the subject rather than
// implying the console. See reaper.ts's seam for the ruling in full.

import type { ReleaseLifecycleStatus } from './fork-resume-gate.js';

/** The state of the machine a release records, as the console can read it
 *  from the row alone (vm-reconcile is what keeps 'gone' honest). */
export type VmCell = 'none' | 'live' | 'stopped' | 'gone';

/** Every VM cell, for the totality assertion. */
export const VM_CELLS: readonly VmCell[] = Object.freeze(['none', 'live', 'stopped', 'gone'] as const);

/** The verb that owns a cell. 'replay/down' is the row-class escape hatch. */
export type VmVerbName =
  | 'vm/provision' | 'vm/relaunch' | 'vm/resume' | 'start' | 'reopen' | 'replay/down';

/** WHICH AMI the verb's next boot actually runs on — the input the pin
 *  invariant below is a function of.
 *
 *   'current-ami'  the verb launches a NEW instance, so the launcher resolves
 *                  whatever AMI is current at RunInstances time;
 *   'existing-ami' the verb starts the RECORDED instance, which boots the AMI
 *                  it was baked from — a fact no publish or rebake changes;
 *   'nothing'      the verb boots no machine (it adopts or ends one). */
export type NextBootAmi = 'current-ami' | 'existing-ami' | 'nothing';

export interface VmVerbAnswer {
  verb: VmVerbName;
  /** The route, or null where the verb is not an HTTP one. */
  route: string | null;
  status: ReleaseLifecycleStatus;
  vm: VmCell;
  boots: NextBootAmi;
  /** What that verb does — the half a bare verb name never says. */
  does: string;
}

/** Read the cell off a release row. `vm_state = 'gone'` is vm-reconcile's
 *  verdict and OUTRANKS the stop stamp: REL-096 carried `vm_state='gone'`
 *  with `vm_stopped_at` NULL (relaunch had cleared it), which is exactly the
 *  combination that made `vm/resume` answer "not stopped — nothing to do"
 *  instead of naming a verb. */
export function vmCellOf(release: {
  vm_instance_id: string | null;
  vm_stopped_at?: Date | string | null;
  vm_state?: string | null;
}): VmCell {
  if (!release.vm_instance_id) return 'none';
  if (release.vm_state === 'gone') return 'gone';
  if (release.vm_stopped_at) return 'stopped';
  return 'live';
}

/** THE TABLE. TOTAL by construction over
 *  RELEASE_STATUSES x VM_CELLS (16 cells) plus the replay row class — every
 *  cell names exactly one verb. The cell this file exists for is
 *  `active` x {none, gone} -> vm/provision. */
export function vmVerbFor(args: {
  status: ReleaseLifecycleStatus;
  vm: VmCell;
  /** 'replay' rows are torn down and recreated, never provisioned. */
  rowClass?: string | null;
}): VmVerbAnswer {
  const { status, vm } = args;
  const seat = (verb: VmVerbName, route: string | null, boots: NextBootAmi, does: string): VmVerbAnswer =>
    ({ verb, route, status, vm, boots, does });
  if (args.rowClass === 'replay') {
    return seat(
      'replay/down', 'POST /api/releases/:id/replay/down', 'nothing',
      'a replay row is torn down and recreated from its checkpoint — it is never relaunched and never provisioned in place',
    );
  }
  if (status === 'completed' || status === 'aborted') {
    return vm === 'stopped'
      ? seat('reopen', 'POST /api/releases/:id/reopen', 'existing-ami',
          'resurrection: the in-window lane ec2-STARTS the recorded instance, so it comes back on the AMI it was baked from')
      : seat('reopen', 'POST /api/releases/:id/reopen', vm === 'live' ? 'nothing' : 'current-ami',
          'resurrection: with no live machine the reopen provisions fresh on the CURRENT AMI; with one it adopts it');
  }
  if (vm === 'stopped') {
    return seat(
      'vm/resume', 'POST /api/releases/:id/vm/resume', 'existing-ami',
      'StartInstances on the RECORDED instance — the idle-stop sweep\'s inverse. Nothing is launched, so the machine comes back on its own baked AMI',
    );
  }
  if (status === 'ideating') {
    return vm === 'live'
      ? seat('start', 'POST /api/releases/:id/start', 'nothing',
          'the first activation LOCKS ONTO the VM already recorded (the create-time eager boot / the first RM turn) — scope lock, flip, deploy, plan kick; nothing is launched')
      : seat('start', 'POST /api/releases/:id/start', 'current-ami',
          'the first activation provisions on the CURRENT AMI, then flips. POST /api/releases/:id/retry-boot is its no-flip twin for a PARKED fork');
  }
  // MIXED-MEANING ACCEPTANCE (#4198, P4 #4256). `releases.status = 'active'` carries TWO
  // meanings for now: "a VM started" on rows stamped before P2 (#4254), "scope was locked"
  // on rows stamped after it. The owner ruled FORWARD-ONLY with no backfill (#4198 comment 1),
  // so this predicate spans both populations knowingly —
  // the fall-through below is the 'active' cell, and the verb it answers is the same under either
  // meaning: the question is what the MACHINE needs, which status does not decide.
  // Nothing here infers which meaning a row carries: the owner declined that heuristic by name.
  // status === 'active'
  if (vm === 'live') {
    return seat(
      'vm/relaunch', 'POST /api/releases/:id/vm/relaunch', 'current-ami',
      'REPLACE the serving machine: forensic snapshot FIRST, TerminateInstances, clear the record, provision fresh on the CURRENT AMI. The snapshot is owed because the SUBJECT is a release VM, NOT because this verb is special and NOT as a console-wide guarantee — every path that terminates a release VM owes the same ordering, and one that cannot audits the destruction by name instead (#3509). The cell is not broken — this verb is for a machine that cannot survive a reboot (#790)',
    );
  }
  return seat(
    'vm/provision', 'POST /api/releases/:id/vm/provision', 'current-ami',
    vm === 'gone'
      ? 'give an Active release its machine back after the recorded one was REAPED (vm_state=\'gone\'): nothing to snapshot, nothing to terminate — that is why relaunch declines — so the ordinary ensure pipeline provisions fresh on the CURRENT AMI (#1272)'
      : 'give an Active release a machine when it records none: the ordinary ensure pipeline provisions fresh on the CURRENT AMI, no status flip (the release is already Active) (#1272)',
  );
}

/** THE PIN-vs-VERB INVARIANT (#1272, the half #1185/#1254 does not reach).
 *
 * A release's boot obeys the SSM parameter `/helm/release/<uuid>/framework-pin`,
 * which OUTRANKS the row (`bootstrap.sh:174`). Nothing in this system says
 * when such a pin is safe, and the reflex answer — "a pin naming a bundle
 * built for a superseded base is stale, sweep it" — is MEASURABLY WRONG.
 *
 * THE CONTROL, measured 2026-09-09: REL-096 and REL-097 carry BYTE-IDENTICAL
 * pins (framework 29327b16, base_input_hash a932a6f3, both written by the
 * quiescent update lane at 21:32:14Z). On REL-096 that pin was FATAL — the
 * relaunch launched a fresh instance from a `6030a4ee` AMI and the pin made it
 * fetch an `a932a6f3` bundle, census row 9 failed, FATAL, reap. On REL-097 the
 * same pin is PROTECTIVE — a resume starts its EXISTING `a932a6f3` instance,
 * and the pin is what keeps that reboot on the bundle the box already runs.
 *
 * So a pin is not stale or fresh on its own. THE INVARIANT IS:
 *
 *     the pin must agree with the AMI the CHOSEN VERB will actually boot.
 *
 * which makes it a property of the VERB, evaluated per release — never a
 * fleet-wide cleanup. A sweep that deleted "stale" pins today would brick
 * every stopped release in the fleet, and `mayClear` is false for exactly
 * those. */
export interface PinRuling {
  /** Must the bundle the box will FETCH be gated against the CURRENT AMI
   *  before this verb runs? True whenever the verb launches a new instance. */
  gateAgainstCurrentAmi: boolean;
  /** Is the SSM override safe to DELETE right now, on its own? True only
   *  when no recorded instance's reboot depends on it. Deliberately FALSE in
   *  the `vm/relaunch` cell: there the clear is a STEP OF THE VERB (#1254,
   *  after the terminate), not a standalone operator action. */
  mayClear: boolean;
  line: string;
}

export function pinRulingFor(a: VmVerbAnswer): PinRuling {
  if (a.boots === 'existing-ami') {
    return {
      gateAgainstCurrentAmi: false,
      mayClear: false,
      line:
        `pin: PROTECTIVE — ${a.verb} starts the RECORDED instance, which boots the AMI it was baked ` +
        `from. The framework pin is what keeps that reboot on the bundle the box already runs; ` +
        `deleting it drops the release back to its launch pin (REL-097's shape). Do not clear it.`,
    };
  }
  if (a.boots === 'nothing') {
    return {
      gateAgainstCurrentAmi: false,
      mayClear: false,
      line: `pin: NOT CONSULTED — ${a.verb} boots no machine in this cell, so no bundle is fetched.`,
    };
  }
  const clearable = a.vm === 'none' || a.vm === 'gone';
  return {
    gateAgainstCurrentAmi: true,
    mayClear: clearable,
    line: clearable
      ? `pin: GATED against the CURRENT AMI — ${a.verb} launches a NEW instance, so the bundle the box ` +
        `will fetch (SSM override, else the row pin, else latest.json) must be built for the current ` +
        `base. No recorded instance depends on the override here, so clearing it is safe (REL-096's shape).`
      : `pin: GATED against the CURRENT AMI — ${a.verb} launches a NEW instance after ending the ` +
        `recorded one. The override is cleared by the VERB itself, after the terminate (#1254) — not ` +
        `as a standalone action while the instance is still serving.`,
  };
}

// ─── THE LAUNCH WINDOW (#2561, enforcing #2549's precondition) ──────────────
//
// #2549 ruled a PRECONDITION, not a defect: no VM launch and no VM resume
// while the P13 (#2529) -> P7 (#2399) window is open, because P13 removed the
// rendered escalation card and P7 re-homes it. The wake RECORD lands either
// way; what a woken agent does not get is the card. Until #2561 the control
// was a person reading messages — `releasevm`'s watch EMITS the window and
// nothing REFUSED on it.
//
// THE REFERENT IS THE PINNED BUNDLE, NEVER THIS CONSOLE'S TREE. A release VM
// runs the boot-fetched BUNDLE (`fetch-framework.sh` over `/opt/aisdlc`), and
// the console deploys on merge while the bundle pointer moves only when
// someone publishes. So a marker read from the tree this module is compiled
// into goes green the moment P7's console-visible half lands, while a VM still
// boots a bundle without it — a gate that opens before the thing it guards is
// true (the split-referent class, #2328). The reading handed to the ruling
// below therefore always NAMES its referent, and a ruling rendered without it
// re-creates the incident — the same discipline `referentCompat` carries in
// framework-versions.ts.
//
// WHY THE MARKER IS A `dist/` PATH AND NOT THE `src/` PATH #2549 NAMES.
// Measured on `ops/release-vm/publish-bundle.sh` at 0b880b02a: the bundle
// stages `TREES=(helm-bridge/dist helm-bridge/public coordination-layer/dist
// pi-stack/dist pi-shared/extensions pi-shared/agents claude-shared)`.
// `helm-bridge/src/` is in NO bundle that has ever been published, so a probe
// for `helm-bridge/src/server/message-service.ts` against the artefact the VM
// installs reads ABSENT forever — a PERMANENT refusal on every launch and
// every resume in the fleet, which is precisely the false refusal #2561's own
// A3 says costs more than a false pass. #2549's sentence is correct about
// MAIN and wrong about the BUNDLE; the compiled twin `helm-bridge/dist/server/
// message-service.js` is the same fact asked of the tree that actually ships.
//
// REACH, STATED SO NOBODY READS MORE INTO IT. This probes the MODULE's
// presence, not the re-homed RENDERING beside it in #2549's sentence. P7 is
// unwritten, so any token for the rendering would be a guess at a string that
// exists only as a template — this repo's own named way of manufacturing a
// zero. Presence of a non-empty compiled `message-service.js` is what the
// probe can honestly answer, and the refusal line says so.
// ─── THE WINDOW HAS TWO BOUNDARIES, AND A ONE-SIDED PREDICATE IS THE A3
//     CATASTROPHE ARRIVING THROUGH THE SPEC RATHER THAN THROUGH A BUG ───────
//
// The first cut of this gate asked ONE question — "is P7 in the pinned bundle"
// — and refused when the answer was no. That is a CONSTANT with respect to the
// transition: P7 is unwritten, so it refused every launch and every resume from
// its first deploy, forever, on a fleet whose escalation card carrier is
// perfectly intact. It shipped green, because all 48 of its arms asserted the
// one-sided behaviour was correct. **A suite cannot detect a wrong premise: the
// arms encode it, and the more thorough the suite the more confidently it
// certifies the mistake.** What caught it was a measurement of the artefact's
// PRODUCER (`publish-bundle.sh`'s `TREES=`) that no arm was pointed at.
//
// A state probe answers "is X true now". A CHANGE detector must also answer
// "and was it false before". So the window is a PAIR:
//
//   carrier PRESENT in the pinned bundle   -> ALLOW   pre-window; nothing lost yet
//   replacement PRESENT in it              -> ALLOW   post-window; rendering re-homed
//   carrier GONE *and* replacement ABSENT  -> REFUSE  the window — the ONLY refusal
//   carrier PRESENT *and* replacement too  -> ALLOW + LOG (halves landed out of order)
//
// Both operands are read from the SAME resolved bundle sha. That is the
// referent clause below, now with two things to read instead of one.
//
// ─── EVERY UNMEASURED OPERAND ALLOWS, LOUDLY ───────────────────────────────
//
// Ruled, and the asymmetry is measured rather than assumed. A false pass inside
// the window costs ONE agent ONE rendered card, and #2549 says in its own words
// that the wake RECORD lands either way. A false refusal costs EVERY launch and
// EVERY resume in the fleet, for as long as nobody notices. And the gate's whole
// lifetime is the window: A6 says it is inert once P7 publishes, so a
// fail-closed unmeasured reading does maximum harm for maximum duration —
// most wrong exactly when the gate should be doing nothing at all.
//
// It is also not the only control. The `console` session refuses launches and
// resumes by judgement regardless of window state. **The machine's job is to
// TELL you; the session's job is to REFUSE.** A machine gate that also refuses
// adds nothing to the enforcement and everything to the blast radius — and the
// seat below MULTIPLIES the ways to be unmeasured (two entries that can go
// missing, several controls that can read 0, plus the artefact read). A gate
// that grows more dangerous as it grows more thorough is the wrong shape.

/** One literal the OPEN side matches, with `grep -aoF` occurrence semantics —
 *  never a regex, never a basename, never a glob. */
export interface SeatToken {
  token: string;
  role: 'sentinel' | 'disambiguator' | 'control';
  /** Sentinels belong to a PAIR. The gap is SHUT while EITHER pair holds, and
   *  OPENS only when BOTH break — one pair going quiet is a refactor, not a
   *  deletion. */
  pair?: 'A' | 'B';
  /** What the GATE asserts. FLOOR only, never an exact count — see below. */
  floor: number;
  /** The EXACT count measured at `measuredAt`. RECORDED HERE, ASSERTED ONLY BY
   *  A TREE WATCH. Not asserted by this gate, ever, and tightening it into an
   *  equality here is a scheduled fleet outage, not a hardening. */
  treeWatchExact?: number;
  /** Why this token is in the seat. */
  why: string;
}

/** One pinned artefact entry and the tokens counted inside it. */
export interface SeatEntry {
  /** The artefact entry path, matched EXACTLY. Never a basename, suffix or
   *  glob: `routeCoordinationEvents` and `writeWakeFile(` also appear in
   *  `coordination-layer/dist/graphstore.js`, and `escalation_delivery` also
   *  appears in `helm-bridge/dist/server/channels.js` (measured at
   *  ba939a07c2f9, `grep -rlaF` over the shipped dists) — a tree-scoped probe
   *  reads `>= 1` forever through those and can never reach GAP OPEN, which is
   *  a guardrail that cannot refuse. The exact path IS the disambiguation;
   *  there is no multiplicity assertion to make. */
  path: string;
  tokens: readonly SeatToken[];
}

/** The OPEN-side seat: DATA, re-pointable without touching the gate's code. */
export interface LaunchWindowSeat {
  entries: readonly SeatEntry[];
  /** The sha `treeWatchExact` was measured at. A count without its referent is
   *  what produced every wrong answer on this unit. */
  measuredAt: string;
  /** False = no token ratified; the OPEN side is UNMEASURED and says so. An
   *  empty seat that says it is empty beats a seat carrying a token nobody has
   *  ratified. */
  ratified: boolean;
}

// WHY A CONTROL'S ASSERTION STRENGTH IS A PROPERTY OF ITS READER, not of the
// token. THE EXACT HALF OF THIS SPLIT IS NOT IMPLEMENTED HERE AND IS TRACKED
// AS #2594 — a tree-by-ref watch that CAN assert the exact counts because it
// names its referent. `treeWatchExact` below is RECORDED and asserted by
// nothing in this module; do not "harden" it into an equality here. A tree watch reads a ref it NAMES, so it can assert EXACT and
// re-measure when it deliberately moves that ref. THIS GATE reads whatever
// `bundles/framework/latest.json` pins AT LAUNCH TIME — it does not choose its
// referent and cannot re-measure on someone else's publish. An EXACT assertion
// here is a promise about an artefact that does not exist yet, and the first
// legitimate bundle that touches any count turns every launch into a refusal.
// "Invariant across four shas" is evidence about four shas.
// ─── RE-POINTED AFTER #2555 (measured at ba939a07c2f9, 2026-09-13) ─────────
//
// The first seat (measured at d05ad765316b) pinned the broker's PUSH lane —
// `escalationActionabilityFor(`, `send(client, evt)` and the broker's
// `kind: "escalation_delivery"` row. P13 (#2404, PR #2529) had already made the
// broker a PRODUCER ONLY and left that machinery defined with zero call sites;
// #2555 (6559970da, 02:03Z 2026-09-13) then DELETED it — `resolveRecipients`,
// `enqueuePush`, `flushPushes`, the only writer of `escalation_delivery` rows —
// by a reviewed design decision. From that commit every bundle built from main
// reads all three broker sentinels at 0 code, pair A and pair B both break, and
// the seat's own disambiguator in wake-executor.js (1 code) makes the OPEN side
// read REFACTORED on an artefact whose delivery path is intact. DRIVEN through
// the real gate against the LIVE pointer (framework_sha ba939a07c…, sha256
// verified): openSide=refactored, close=present, refuses=false — the
// `close === 'present'` allow (P7 #2399 landed 50059e5b3, BEFORE #2555) sits
// ahead of the REFACTORED refusal, so no launch was refused; but the reading
// was WRONG about the artefact, and against any bundle whose CLOSE side is not
// present it refuses every launch and every resume. So the seat is re-pointed
// to the path the broker actually delivers on now, and the pre-fix seat is
// pinned as a fixture in `launch-window.test.mjs` (arm P).
//
// WHAT THE BROKER EMITS NOW, and why the broker.js entry STAYS. Every
// graph-derived event — holder escalations included — goes through
// `routeCoordinationEvents` (wired at `onEvents: routeCoordinationEvents`),
// which writes exactly one audit row `to: "<wake-queue>"` (the #2504 second
// instrument) and one durable intake record via `writeWakeFile(...)`. That is
// the delivery path; it passes through broker.js, so the entry keeps a pair
// there. Pair A is those three CODE SHAPES; its disambiguator is the bare
// producer name `routeCoordinationEvents` (2 code: the definition and the
// wiring), which a re-key of the row or the hookup leaves behind and a
// deletion of the producer does not. The `logRecord(` control's exact count
// moved 16 -> 14: the two calls that sat inside the deleted push block went
// with it, which is why it is FLOOR-only. Counts below were measured with the
// gate's own `countToken` on BOTH `coordination-layer/src/broker.ts` and the
// built `dist/broker.js`, and on both halves of wake-executor; every token
// reads the same in src and dist.
//
// PAIR B IS THE WAKE-EXECUTOR'S OWN, and at ba939a07c2f9 it had two members
// again: the delivery watchdog's coordlog branch (its `rec.kind` test on the
// `escalation_delivery` literal) and the watch entry it built on that same
// branch, `kind: 'escalation', holder`, with the bare `escalation_delivery`
// (1 code, 2 comment) as its entry-scoped disambiguator because channels.js
// carries the literal too.
//
// ─── RE-POINTED AGAIN AFTER #2402 (measured at abea72a05cd6, 2026-09-13) ───
//
// #2402 (P11, 60e68aeb7, 10:48Z 2026-09-13, #2388) deleted that coordlog
// arm: its only writer had gone with #2722, so the branch was DEAD INPUT, and
// the escalation watch is now armed from the executor's OWN delivery ledger —
// `recordDelivery` calls `watchEscalationDelivery(entry)`, which builds the
// same `kind: 'escalation', holder` watch entry from an arming ledger row.
// #2872 recorded the seat above at its base ba939a07c2f9, 1h50m AFTER the
// token had left main: that `rec.kind` test (the seat's first pair-B
// sentinel, verbatim in the pre-2402 fixture) reads 1 at ba939a07c2f9 and
// at 60e68aeb7^, 0 at 60e68aeb7 and at every main since
// (d110a45c6, a1767474db29, abea72a05cd6), and the old disambiguator reads
// 0 code / 3 comment there — the three comments are the ones explaining the
// deletion. DRIVEN on the built dists at a1767474db29: that seat reads
// openSide=shut on pair A ALONE ("pair A holds"), so the live gate ALLOWS
// (close side present -> post-window allow) and pair B is BLIND, not
// refusing — a seat with one working pair is one refactor of pair A away
// from a REFACTORED refusal of every launch and every resume. CI could not
// see it: the console job builds helm-console only, so `launch-window.test.mjs`
// printed `built dist NOT RUN` and 98 passed; with the three dists built it
// read 100 passed, 4 failed (P5, P6, P8, P9 [built dist]).
//
// So pair B is re-pointed onto the ledger-fed watch: the call
// `this.watchEscalationDelivery(entry)` inside `recordDelivery` (the wiring —
// delete it and no ledger row arms a watch) and the watch entry it builds,
// `kind: 'escalation', holder` (unchanged). The disambiguator is the bare
// method name `watchEscalationDelivery` (2 code — the definition and the
// call — 1 comment), entry-scoped by rule even though at abea72a05cd6 no
// other shipped dist carries it (`grep -rlaF` over helm-bridge/dist: 1 file).
// Every count below was measured with the gate's own `countToken` on the
// BUILT `helm-bridge/dist/server/wake-executor.js`, comments stripped. The
// pre-#2402 seat is pinned as
// `scripts/fixtures/launch-window-seat.pre-2402.abea72a05.json` and driven
// as a PRE-FIX control in `launch-window.test.mjs` (arm P), beside the
// pre-#2555 one.
// ─── RE-POINTED AFTER D2 (OR-46; measured at 844fab541b72, 2026-09-22) ─────────
// D2 (#5003) deleted the Bridge's engineer-dispatch block, and with it
// `ENGINEER_RESTART_TTL_MS` — the wake-executor entry's CONTROL token since
// #2555. A control that reads 0 is BROKEN PROBE by construction, so at
// 844fab541 the FIXED seat refused every launch and every resume on a tree
// whose delivery path was intact (launch-window.test.mjs: 107 passed,
// 7 failed — P5/P6/P8/P9/P10/P12/P13 [built dist]). The control is re-pointed
// onto `LANE_CAP_RE`, a module constant (the cap-shaped `blocked_on` key
// regex, wake-executor.ts) that is OFF the escalation-delivery path the pair-B
// sentinels watch and that D2 deliberately kept. Every count below was
// re-measured with the gate's own `countToken` on the BUILT dists at
// 844fab541b72, comments stripped: the nine surviving tokens read exactly what
// abea72a05cd6 recorded; the new control reads 3 code, 0 comment. The pre-D2
// seat is pinned as `scripts/fixtures/launch-window-seat.pre-d2.844fab541.json`
// and driven in launch-window.test.mjs (arm P14) beside the two older pins —
// on the built dist all three now read BROKEN PROBE naming the deleted control,
// which is the reading this re-point exists to end.
// ─── RE-RECORDED AFTER D7b (OR-46; measured at d41b598bece3, 2026-09-22) ───────
// D7b (5e316d343, member D7 60a657ef5) deleted the broker's fourteen retired
// team-channel verb cases (whisper / join / leave / subchannel_*), and three of
// the `logRecord(` calls sat inside them: coordination-layer/src/broker.ts reads
// 14 `logRecord(` at 5e316d343^ and 11 at 5e316d343. That is a CONTROL's exact
// count moving with an off-path deletion — no token re-points, nothing is
// widened: the delivery block's one audit-row call is untouched, the floor (8)
// is unchanged, and the gate (floor-only) read the same verdict before and
// after. Only the tree watch moved: at d41b598bece3 (the deletion tip) the
// P9 [built dist] watch read `logRecord(@coordination-layer/dist/broker.js=11
// (recorded 14)`, 1 off of 10. Re-recorded with the gate's own `countToken`
// on the BUILT dists at d41b598bece3, comments stripped: the other nine tokens
// read exactly what 844fab541b72 recorded, src and dist agree for all ten, and
// `logRecord(` reads 11 code, 0 comment. launch-window.test.mjs P3d reads the
// pre-re-record seat block and broker.ts out of git at that same sha, so the
// arm that proves the move is not computed from this record.
export const LAUNCH_WINDOW_SEAT: LaunchWindowSeat = Object.freeze({
  measuredAt: 'd41b598bece3',
  ratified: true,
  entries: Object.freeze([
    Object.freeze({
      path: 'coordination-layer/dist/broker.js',
      tokens: Object.freeze([
        { token: 'onEvents: routeCoordinationEvents', role: 'sentinel', pair: 'A', floor: 1, treeWatchExact: 1,
          why: 'pair A: the wiring that hands every graph-derived event to the producer — delete it and nothing is delivered' },
        { token: 'to: "<wake-queue>"', role: 'sentinel', pair: 'A', floor: 1, treeWatchExact: 1,
          why: 'pair A: the audit row\'s target, narrowed to the CODE SHAPE (the #2504 second instrument sits on this line)' },
        { token: 'writeWakeFile(worktree, ev, snapshot, holderScope', role: 'sentinel', pair: 'A', floor: 1, treeWatchExact: 1,
          why: 'pair A: the intake write — the durable record the wake executor consumes; the call, not the definition' },
        { token: 'routeCoordinationEvents', role: 'disambiguator', floor: 0, treeWatchExact: 2,
          why: 'entry-scoped: the producer\'s bare name survives a re-key of the row or the hookup and tells REFACTORED from GAP OPEN' },
        { token: 'LIVENESS_PATH', role: 'control', floor: 1, treeWatchExact: 3,
          why: 'off-path and invariant — proves the probe read this entry at all' },
        { token: 'logRecord(', role: 'control', floor: 8, treeWatchExact: 11,
          why: 'large population; 1 of its calls is the audit row INSIDE the delivery block, so it is ON-path and FLOOR-only by nature (16 -> 14 across #2555, 14 -> 11 across D7b: three calls left with the retired channel verb cases)' },
      ] as readonly SeatToken[]),
    } as SeatEntry),
    Object.freeze({
      path: 'helm-bridge/dist/server/wake-executor.js',
      tokens: Object.freeze([
        { token: 'this.watchEscalationDelivery(entry)', role: 'sentinel', pair: 'B', floor: 1, treeWatchExact: 1,
          why: 'pair B: the wiring — recordDelivery hands every ledger row to the ledger-fed watch (#2402 replaced the coordlog arm with it); the call, not the definition' },
        { token: "kind: 'escalation', holder", role: 'sentinel', pair: 'B', floor: 1, treeWatchExact: 1,
          why: 'pair B: the watch entry watchEscalationDelivery builds from an arming ledger row — two code shapes on one path, so the pair rule has two members' },
        { token: 'watchEscalationDelivery', role: 'disambiguator', floor: 0, treeWatchExact: 2,
          why: 'entry-scoped by rule: the bare method name (definition + call) survives a re-key of the call or of the entry and tells REFACTORED from GAP OPEN' },
        { token: 'LANE_CAP_RE', role: 'control', floor: 1, treeWatchExact: 3,
          why: 'off-path and invariant (a module constant: the cap-shaped blocked_on key regex) — proves the probe read this entry at all; replaced ENGINEER_RESTART_TTL_MS, which left with the engineer-dispatch block (D2, OR-46)' },
      ] as readonly SeatToken[]),
    } as SeatEntry),
  ] as readonly SeatEntry[]),
} as LaunchWindowSeat);

export interface LaunchWindowSpec {
  /** The issue carrying the ruling this gate enforces. */
  issue: number;
  /** The phase whose landing CLOSES the window. */
  phase: string;
  /** Bundle-relative paths that must all be present and non-empty — the CLOSE
   *  side, unchanged: P7 present BY CONTENT in the pinned bundle. */
  bundleEntries: readonly string[];
  /** The path #2549's sentence names — kept so the refusal can explain the
   *  translation rather than look like a different claim. */
  srcOrigin: string;
  clears: string;
  seat: LaunchWindowSeat;
}

export const LAUNCH_WINDOW: LaunchWindowSpec = Object.freeze({
  issue: 2549,
  phase: 'P7 #2399',
  bundleEntries: Object.freeze(['helm-bridge/dist/server/message-service.js']),
  srcOrigin: 'helm-bridge/src/server/message-service.ts',
  clears:
    'publish a framework bundle built from a main that carries P7 #2399 ' +
    '(ops/release-vm/publish-bundle.sh), then launch or resume again',
  seat: LAUNCH_WINDOW_SEAT,
} as LaunchWindowSpec);

/** One token's occurrences in one artefact entry, split by where they sit.
 *  Declared here, with the ruling, because the ruling asserts on it — the
 *  reader that produces it lives on the impure side of the seam. */
export interface TokenCount {
  token: string;
  /** Occurrences on the CODE half of a line. This is what the seat asserts. */
  code: number;
  /** Occurrences on the comment half. Reported, never asserted. */
  comment: number;
  /** Occurrences ignoring the split — the counting-question control. */
  raw: number;
}

/** What the probe read out of ONE pinned artefact entry. */
export interface EntryReading {
  path: string;
  /** False = the pinned entry is MISSING from the artefact -> BROKEN READ.
   *  A pinned entry that EXISTS and yields 0 code hits is an ANSWER. */
  present: boolean;
  counts: readonly TokenCount[];
}

/** THE CLOSE side: is P7 in the pinned bundle by content? */
export type CloseSideVerdict = 'present' | 'absent' | 'unmeasured';

/** THE OPEN side. Only `gap-open` can contribute to a refusal. */
export type OpenSideVerdict =
  | 'shut'          // a pair holds — the carrier is intact, we are PRE-window
  | 'gap-open'      // every pair broken and no disambiguator — the carrier is gone
  | 'refactored'    // sentinel gone, literal still here — a re-key, not a gap
  | 'broken-probe'  // a CONTROL read below its floor — the probe is wrong
  | 'broken-read'   // a pinned entry is missing from the artefact
  | 'unmeasured';   // the seat is not ratified, or nothing was read

/** THE THREE CONTROL LAYERS, and they are NOT redundant. Collapsing them into
 *  one "is it working" check is what they look like from outside, and it loses
 *  the only distinctions that matter:
 *
 *    artefact-wide  `control helm-bridge/dist/* = N`  -> a TRUNCATED or empty
 *                   download. Fires EARLIEST, and is the only one that can
 *                   tell "the download failed" from "the frame is gone".
 *    entry          a pinned entry missing from the artefact -> BROKEN READ.
 *    token          LIVENESS_PATH / LANE_CAP_RE -> BROKEN PROBE.
 *                   The read completed and the PROBE is wrong — the only layer
 *                   that can tell that from "the answer is genuinely zero". */
export interface FrameworkWindowReading {
  /** The framework sha the pointer RESOLVED to; null when unresolvable. */
  sha: string | null;
  /** The artefact sha256 this run VERIFIED, before reading a byte out of it.
   *  Null = not verified, which is itself unmeasured. */
  sha256Verified: string | null;
  /** WHICH object this reading is about — always printed beside the verdict. */
  referent: string;
  /** False = no `releaseVm.bundle` seat; this console launches no release VM. */
  armed: boolean;
  /** The artefact-wide control: entries seen under `helm-bridge/dist/`. 0 on a
   *  complete artefact is impossible, so 0 means the download, not the frame. */
  artefactControl: number;
  entries: readonly EntryReading[];
  close: CloseSideVerdict;
  /** The probe's own measured lines. */
  measured: readonly string[];
  /** Set when the probe could not get far enough to produce entries at all. */
  unreadable?: string;
}

export interface LaunchWindowRuling {
  /** Does this verb refuse right now? */
  refuses: boolean;
  /** Is the #2549 window open for the referent that was read? */
  open: boolean;
  /** The OPEN side's own verdict, so a caller can branch without parsing prose. */
  openSide: OpenSideVerdict;
  close: CloseSideVerdict;
  /** True when the gate REFUSED because it could not MEASURE its operand,
   *  rather than because it measured an open window. Under a fail-closed gate
   *  this is the operationally important distinction and the two have
   *  different remedies: a measured window clears when P7 publishes, an
   *  unmeasured operand clears when someone fixes the probe. An operator
   *  staring at a blocked fleet needs to know which one they are in. */
  refusedUnmeasured: boolean;
  line: string;
}

/** REFACTORED's disposition — ONE constant, and it is RULED.
 *
 *  REFUSE. Every unmeasured-family verdict refuses: REFACTORED, BROKEN PROBE,
 *  BROKEN READ, and any operand this gate could not read. Only three states
 *  allow — the carrier present, the replacement present, and a verb that boots
 *  no machine.
 *
 *  THIS WAS ARGUED THE OTHER WAY FIRST, AND THE OBJECTION WAS REAL. A refusing
 *  REFACTORED is reachable by a lane doing legitimate work in
 *  `coordination-layer/` or `helm-bridge/` that has never heard of this gate —
 *  a fleet outage armed by an unrelated PR. The answer is STRUCTURAL, not
 *  directional, and it is better than either direction: **#2595 drives this
 *  gate against the BUILT dists on every PR touching those trees**, so the
 *  refactor that would REFACTORED the gate goes red on the PR that did it and
 *  never on the fleet. The outage moves to the pull request.
 *
 *  Keep that in view if anyone reopens this: the question is not which way to
 *  fail, it is where the failure surfaces. Until #2595 lands, the REFACTORED
 *  refusal text below is the only thing standing between an unrelated refactor
 *  and an operator with no idea why launches stopped — which is why it names
 *  the entry, the token and the remedy rather than saying a sentinel moved.
 *
 *  AND THE FLOOR CONTROLS ABOVE ARE LOAD-BEARING BECAUSE OF THIS LINE, not
 *  merely correct. With the gate failing closed, an EXACT control asserted
 *  against an artefact this gate does not choose is a SCHEDULED fleet outage:
 *  the first legitimate bundle publish that moves any count refuses every
 *  launch. FLOOR is what prevents it. Do not tighten them; #2594 is where
 *  exact counts belong, against a ref their reader controls.
 *
 *  A gate that allows when it cannot read its sentinel is a vacuous green
 *  wearing a launch verb. */
export const REFACTORED_DISPOSITION: 'refuse' | 'allow' = 'refuse';

/** THE OPEN SIDE, derived PURELY from counts. Precedence is load-bearing:
 *  broken-read beats broken-probe beats shut beats refactored beats gap-open,
 *  because a gate that cannot read its operand must never reach a verdict
 *  ABOUT that operand. */
export function openSideVerdictOf(
  r: FrameworkWindowReading,
  seat: LaunchWindowSeat = LAUNCH_WINDOW_SEAT,
): { verdict: OpenSideVerdict; why: string } {
  if (!seat.ratified) {
    return { verdict: 'unmeasured', why: 'the OPEN-side seat carries no ratified token' };
  }
  if (r.unreadable) return { verdict: 'broken-read', why: r.unreadable };
  if (r.artefactControl === 0) {
    return {
      verdict: 'broken-read',
      why: 'the artefact yielded 0 entries under helm-bridge/dist/ — impossible for a complete ' +
        'bundle, so this is a TRUNCATED or empty download and not a missing frame',
    };
  }
  const byPath = new Map(r.entries.map((e) => [e.path, e]));
  for (const se of seat.entries) {
    const got = byPath.get(se.path);
    if (!got || !got.present) {
      return { verdict: 'broken-read', why: `pinned entry ${se.path} is MISSING from the artefact` };
    }
  }
  const codeOf = (path: string, token: string): number =>
    byPath.get(path)?.counts.find((c) => c.token === token)?.code ?? 0;

  // Token layer: a control below its FLOOR means the read completed and the
  // PROBE is wrong. Never a firing, never a verdict about the frame.
  for (const se of seat.entries) {
    for (const t of se.tokens) {
      if (t.role !== 'control') continue;
      const n = codeOf(se.path, t.token);
      if (n < t.floor) {
        return {
          verdict: 'broken-probe',
          why: `control ${t.token} in ${se.path} = ${n} code, below its floor ${t.floor} — ` +
            'the entry was read and the probe did not find what every honest build carries',
        };
      }
    }
  }
  // The pair predicate. SHUT while EITHER pair holds; a single pair going quiet
  // is a refactor of that pair, not a deletion of the frame.
  const pairs = new Map<string, SeatToken[]>();
  for (const se of seat.entries) {
    for (const t of se.tokens) {
      if (t.role !== 'sentinel' || !t.pair) continue;
      const list = pairs.get(t.pair) ?? [];
      list.push(t);
      pairs.set(t.pair, list);
    }
  }
  const holds: string[] = [];
  for (const [name, tokens] of pairs) {
    const all = tokens.every((t) => {
      const se = seat.entries.find((e) => e.tokens.includes(t));
      return se ? codeOf(se.path, t.token) >= t.floor : false;
    });
    if (all) holds.push(name);
  }
  if (holds.length > 0) {
    return { verdict: 'shut', why: `pair ${holds.join(' and ')} holds — the carrier is intact` };
  }
  // Every pair broken. Entry-scoped disambiguators tell a re-key from a gap —
  // and the verdict names EVERY entry whose literal survives, by FILE. The
  // first cut named only the first surviving entry and the ruling then said
  // "in the same entry", which on the post-#2555 artefact pointed a reader at
  // broker.js while the surviving literal was in wake-executor.js.
  // ONE rule, shared with the dist gate (#2595): `rekeyedEntries` is the
  // reader of the disambiguator for both this verdict and the per-entry
  // shape the CLI prints beside a sentinel below its floor.
  const surviving = rekeyedEntries(r, seat);
  if (surviving.length > 0) {
    return {
      verdict: 'refactored',
      why: 'every sentinel pair is broken but the bare literal survives in code: ' +
        surviving.map((x) => `${x.token} in ${x.path} (${x.code} code)`).join('; ') +
        ' — the frame was RE-KEYED, so this seat needs re-pointing before it can answer',
    };
  }
  return {
    verdict: 'gap-open',
    why: 'every sentinel pair is broken and no disambiguator remains in any pinned entry ' +
      `[${seat.entries.map((e) => e.path).join(', ')}] — the frame is gone ` +
      'OR the literal left this entry',
  };
}

/** Every entry whose disambiguator still has CODE hits — the per-entry fact a
 *  REFACTORED verdict is built from, so the refusal can name the FILE that
 *  actually holds each surviving literal rather than the first entry in the
 *  seat. Pure. Empty when no literal survives anywhere (GAP OPEN's premise).
 *  The SAME reader as `rekeyedEntries` (below) under the name the ruling text
 *  uses — an alias, not a second implementation, so the verdict, the ruling's
 *  refusal sentence and the dist gate (#2595) can never disagree about what a
 *  re-key is. */
export const survivingDisambiguators: typeof rekeyedEntries = rekeyedEntries;

/** Disambiguator code hits NOT attributable to a sentinel's own line, per
 *  entry. Measured at abea72a05cd6: 1 on the broker side (the
 *  `function routeCoordinationEvents(` definition — the wiring sentinel
 *  accounts for the other) and 1 on the wake-executor side (the
 *  `watchEscalationDelivery(entry) {` definition — the call sentinel
 *  accounts for the other). At ba939a07c2f9 the wake-executor side read 0,
 *  where the disambiguator's only code occurrence WAS its sentinel's
 *  occurrence — a shape with two consequences a reader should not have to
 *  infer, and which is why the number is PRINTED:
 *
 *    - `sentinel >= 1` implies `disambiguator >= 1` always, so in the SHUT
 *      state the disambiguator adds nothing.
 *    - REFACTORED can therefore only fire for a re-key that leaves the bare
 *      literal inside the SAME pinned entry. The likeliest real refactor —
 *      hoisting the literal into a shared constants module — removes it from
 *      both entries, and the gate reads GAP OPEN on a tree whose frame is
 *      intact. That is why GAP OPEN's text says "the frame is gone OR the
 *      literal left this entry" and must not be restated as a bare assertion
 *      that the frame is missing. The state names read richer than the arm's
 *      reach, and a design trusted for reach it does not have is worse than
 *      one that states its limit. */
export function outsideSentinelHits(
  r: FrameworkWindowReading,
  seat: LaunchWindowSeat = LAUNCH_WINDOW_SEAT,
): Array<{ path: string; token: string; outside: number }> {
  const out: Array<{ path: string; token: string; outside: number }> = [];
  const byPath = new Map(r.entries.map((e) => [e.path, e]));
  for (const se of seat.entries) {
    const dis = se.tokens.find((t) => t.role === 'disambiguator');
    if (!dis) continue;
    const counts = byPath.get(se.path)?.counts ?? [];
    const total = counts.find((c) => c.token === dis.token)?.code ?? 0;
    // Every sentinel in this entry whose literal CONTAINS the disambiguator
    // contributes one disambiguator occurrence per sentinel occurrence.
    const attributed = se.tokens
      .filter((t) => t.role === 'sentinel' && t.token.includes(dis.token))
      .reduce((n, t) => n + (counts.find((c) => c.token === t.token)?.code ?? 0), 0);
    out.push({ path: se.path, token: dis.token, outside: Math.max(0, total - attributed) });
  }
  return out;
}

/** THE RULING — pure, like every other function in this file. Facts in,
 *  verdict out: no S3, no clock, no DB.
 *
 *  A6 — IT CANNOT OUTLIVE ITS CAUSE, BY CONSTRUCTION. The only verdict that
 *  refuses is one where the bundle does NOT carry the marker. The day P7 is in
 *  the published bundle every cell of this table reads 'carries' and this
 *  function is a no-op on every path — so there is no removal follow-up to
 *  schedule and none is scheduled. Deleting it later is tidying, never a fix. */
export function launchWindowRulingFor(
  a: VmVerbAnswer,
  r: FrameworkWindowReading,
  spec: LaunchWindowSpec = LAUNCH_WINDOW,
): LaunchWindowRuling {
  const subject = `${a.verb}${a.route ? ` (${a.route})` : ''}`;
  const seat = spec.seat;
  const at =
    `bundle ${r.sha ?? 'UNRESOLVED'} (${r.referent}), artefact sha256 ` +
    `${r.sha256Verified ?? 'NOT VERIFIED'}`;
  const open = openSideVerdictOf(r, seat);
  const base = { openSide: open.verdict, close: r.close };

  // The pin ruling's own 'NOT CONSULTED' cell: a verb that boots no machine
  // wakes no agent, so the window has nothing to say about it.
  if (a.boots === 'nothing') {
    return {
      ...base, refuses: false, open: false, refusedUnmeasured: false,
      line: `launch window #${spec.issue}: NOT CONSULTED — ${subject} boots no machine in this cell.`,
    };
  }
  if (!r.armed) {
    return {
      ...base, refuses: false, open: false, refusedUnmeasured: false,
      line:
        `launch window #${spec.issue}: NOT ARMED — this console resolves no framework bundle, so it ` +
        `launches no release VM for the window to guard.`,
    };
  }

  // ── ALLOW: THE THREE MEASURED STATES THAT ARE NOT THE WINDOW ────────────
  // Ordered first, deliberately. Everything that is not one of these refuses,
  // so the allow list is the thing a reader must be able to audit at a glance.
  if (open.verdict === 'shut' && r.close === 'present') {
    // Not a refusal, and worth an operator seeing: the two halves of P7 landed
    // out of order — the replacement is in while the carrier is still there.
    return {
      ...base, refuses: false, open: false, refusedUnmeasured: false,
      line:
        `launch window #${spec.issue}: CLOSED, OUT OF ORDER — at ${at} the carrier is still present ` +
        `(${open.why}) AND ${spec.phase} is already present. Both halves landed; nothing is lost and ` +
        `nothing is refused. Worth a look: the transition did not run in the expected order.`,
    };
  }
  if (open.verdict === 'shut') {
    return {
      ...base, refuses: false, open: false, refusedUnmeasured: false,
      line:
        `launch window #${spec.issue}: NOT OPEN (pre-window) — at ${at} the escalation-card carrier is ` +
        `intact (${open.why}), so nothing is lost yet and ${spec.phase} is not owed. Nothing refused.`,
    };
  }
  if (r.close === 'present') {
    return {
      ...base, refuses: false, open: false, refusedUnmeasured: false,
      line:
        `launch window #${spec.issue}: CLOSED (post-window) — at ${at} the bundle carries ${spec.phase} ` +
        `by content [${spec.bundleEntries.join(', ')}]; the rendering is re-homed. Nothing refused.`,
    };
  }

  // ── REFUSE: THE MEASURED WINDOW ─────────────────────────────────────────
  if (open.verdict === 'gap-open' && r.close === 'absent') {
    return {
      ...base, refuses: true, open: true, refusedUnmeasured: false,
      line:
        `${subject} REFUSED by launch window #${spec.issue}: at ${at}, the escalation-card carrier is ` +
        `GONE (${open.why}) and ${spec.phase} is ABSENT — ${spec.bundleEntries.join(', ')} is not in the ` +
        `published artefact (${spec.srcOrigin} in the source tree). The wake RECORD lands, the rendered ` +
        `card does not, so a woken agent would be given a machine and no card (#2529 removed it, ` +
        `${spec.phase} re-homes it). Both operands read from the PINNED BUNDLE, never this console's ` +
        `own tree. To clear: ${spec.clears}.`,
    };
  }

  // ── REFUSE: THE RE-KEY, NAMED DOWN TO THE FILE AND THE TOKEN ────────────
  // Until #2595 drives this gate against the built dists in CI, this sentence
  // is the ONLY thing between an unrelated refactor and an operator with no
  // idea why launches stopped. It names what moved and what to do, because
  // "a sentinel no longer matches" is not an actionable fact.
  if (open.verdict === 'refactored') {
    const stale = staleSentinels(r, seat);
    const where = stale.length
      ? stale.map((x) => `${x.token} in ${x.path}`).join('; ')
      : '(no sentinel identified — report this, it should not happen)';
    // PER-ENTRY HONESTY: the literal that makes this a re-key rather than a
    // deletion is named WITH THE FILE THAT HOLDS IT. On the post-#2555
    // artefact every stale sentinel was in broker.js and the surviving literal
    // was in wake-executor.js — "in the same entry" was false.
    const surviving = survivingDisambiguators(r, seat);
    const survivesIn = surviving.length
      ? surviving.map((x) => `${x.token} in ${x.path} (${x.code} code)`).join('; ')
      : '(none — report this, REFACTORED without a surviving literal should not happen)';
    return {
      ...base, refuses: true, open: true, refusedUnmeasured: true,
      line:
        `${subject} REFUSED by launch window #${spec.issue}: at ${at}, the escalation-card frame was ` +
        `RE-KEYED. These seat sentinels no longer match the published artefact: ${where} — while the ` +
        `bare literal still has code hits in the pinned artefact: ${survivesIn} — which is what tells ` +
        `a re-key apart from a deletion. NOTHING IS WRONG WITH THE FLEET and ${spec.phase} is not owed; this gate cannot ` +
        `answer until its seat is re-pointed. REMEDY: open the named entry in the published bundle, ` +
        `find what the frame is keyed on now, and update LAUNCH_WINDOW_SEAT in ` +
        `helm-console/src/server/vm-verb-table.ts to match — then launch or resume again. If you are ` +
        `the lane that just re-keyed it, this refusal is yours and #2595 is the arm that should have ` +
        `caught it on your PR instead of here.`,
    };
  }

  // ── REFUSE: EVERY OPERAND THIS GATE COULD NOT READ ──────────────────────
  // BROKEN PROBE, BROKEN READ, an unratified seat, an unreadable CLOSE side.
  // A gate that allows when it cannot read its sentinel is a vacuous green
  // wearing a launch verb.
  const cause =
    open.verdict !== 'gap-open'
      ? `OPEN side ${open.verdict.toUpperCase().replace('-', ' ')} — ${open.why}`
      : `OPEN side GAP OPEN (${open.why}) but the CLOSE side is UNMEASURED — whether ${spec.phase} ` +
        `replaced it could not be established`;
  return {
    ...base, refuses: true, open: false, refusedUnmeasured: true,
    line:
      `${subject} REFUSED by launch window #${spec.issue}: THE OPERAND COULD NOT BE MEASURED, so the ` +
      `window was NOT established and this is not a claim that it is open. ${cause}. At ${at}. ` +
      `A gate that allows when it cannot read its sentinel is a vacuous green wearing a launch verb, ` +
      `so it refuses instead. REMEDY: fix the PROBE, not the fleet — this clears when the read ` +
      `succeeds, not when ${spec.phase} publishes.`,
  };
}

/** The entries whose DISAMBIGUATOR still has code hits — the bare literal is
 *  still inside the pinned entry. This is the one reader of the re-key rule:
 *  `openSideVerdictOf` turns a non-empty answer into REFACTORED, and the dist
 *  gate (#2595, `scripts/launch-window-dist-gate.mjs`) uses the same answer
 *  to label a sentinel shortfall REFACTORED (entry listed here) or GAP OPEN
 *  (not listed) — so the two can never disagree about what a re-key is.
 *  Pure, like everything else here. */
export function rekeyedEntries(
  r: FrameworkWindowReading,
  seat: LaunchWindowSeat = LAUNCH_WINDOW_SEAT,
): Array<{ path: string; token: string; code: number }> {
  const byPath = new Map(r.entries.map((e) => [e.path, e]));
  const out: Array<{ path: string; token: string; code: number }> = [];
  for (const se of seat.entries) {
    const dis = se.tokens.find((t) => t.role === 'disambiguator');
    if (!dis) continue;
    const code = byPath.get(se.path)?.counts.find((c) => c.token === dis.token)?.code ?? 0;
    if (code >= 1) out.push({ path: se.path, token: dis.token, code });
  }
  return out;
}

/** Every seat token whose CODE count sits below its floor, whatever its role
 *  — the whole floor audit in one pure pass, shared by the gate's refusal text
 *  (`staleSentinels`, the sentinel subset) and by the dist gate (#2595), which
 *  asserts EVERY sentinel and EVERY control at its floor on a built tree. A
 *  disambiguator's floor is 0, so it can never appear here. */
export function floorShortfalls(
  r: FrameworkWindowReading,
  seat: LaunchWindowSeat = LAUNCH_WINDOW_SEAT,
): Array<{ path: string; token: string; role: SeatToken['role']; code: number; floor: number }> {
  const byPath = new Map(r.entries.map((e) => [e.path, e]));
  const out: Array<{ path: string; token: string; role: SeatToken['role']; code: number; floor: number }> = [];
  for (const se of seat.entries) {
    for (const t of se.tokens) {
      const code = byPath.get(se.path)?.counts.find((c) => c.token === t.token)?.code ?? 0;
      if (code < t.floor) out.push({ path: se.path, token: t.token, role: t.role, code, floor: t.floor });
    }
  }
  return out;
}

/** Which sentinels have gone quiet, for a refusal that has to name them.
 *  The sentinel subset of `floorShortfalls`. Pure, like everything else here. */
export function staleSentinels(
  r: FrameworkWindowReading,
  seat: LaunchWindowSeat = LAUNCH_WINDOW_SEAT,
): Array<{ path: string; token: string; code: number; floor: number }> {
  return floorShortfalls(r, seat)
    .filter((x) => x.role === 'sentinel')
    .map(({ path, token, code, floor }) => ({ path, token, code, floor }));
}

/** Render the whole matrix as measured lines — the shape this codebase wants
 *  instead of a claim. Used by the console's run line and by the suite. */
export function vmVerbTableLines(statuses: readonly ReleaseLifecycleStatus[]): string[] {
  const out: string[] = [];
  for (const status of statuses) {
    for (const vm of VM_CELLS) {
      const a = vmVerbFor({ status, vm });
      const p = pinRulingFor(a);
      out.push(
        `status '${status}' + vm '${vm}' -> ${a.verb}${a.route ? ` (${a.route})` : ''}; ` +
          `boots ${a.boots}; pin gate ${p.gateAgainstCurrentAmi ? 'YES' : 'no'}, ` +
          `standalone clear ${p.mayClear ? 'SAFE' : 'REFUSED'}`,
      );
    }
  }
  return out;
}
